import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { useItemCache } from '../context/ItemCacheContext';
import { useDocumentLineLookups } from './useDocumentLineLookups';
import { DocumentLineItemWorkOrder, Item } from '../types/Types';
import { parseDecimalInput, unitPriceExcelFormat } from '../utils/decimalInput';
import * as XLSX from 'xlsx';

interface UseNestedDocumentExcelParams {
  documentId: string;
  defaultPartner?: { _id: string; name?: string } | null;
  columnOrder: string[];
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  localRows: DocumentLineItemWorkOrder[];
  setLocalRows: React.Dispatch<React.SetStateAction<DocumentLineItemWorkOrder[]>>;
}

/**
 * Excel export/import for NestedDocumentTable's rows (bulk item lookup by ident).
 * Extracted 1:1 from NestedDocumentTable.tsx (CLAUDE.md rule 9 split) — no behaviour change.
 * NOTE: handleUploadExcel is preserved as-is from the source file, but was already unwired
 * there — the toolbar is only given handleDownloadExcel (no handleExcelUpload prop), so this
 * function was dead code before the split too. Flagging per instructions, not fixing here.
 */
export function useNestedDocumentExcel({
  documentId,
  defaultPartner,
  columnOrder,
  inputRefs,
  localRows,
  setLocalRows,
}: UseNestedDocumentExcelParams) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { addItemsToCache } = useItemCache();
  const { lookupItemsByIdents } = useDocumentLineLookups();

  const asExcelNumber = (value: unknown): number | '' => parseDecimalInput(value) ?? '';

  // Mirrors useNestedDocumentRows's buildDefaultWarehouse (same defaultPartner source) —
  // duplicated rather than threaded across hooks since it's a trivial pure helper.
  const buildDefaultWarehouse = () => {
    if (!defaultPartner || !defaultPartner._id) return null;
    return { _id: defaultPartner._id, name: defaultPartner.name || '' };
  };

  // Excel download functionality
  const handleDownloadExcel = () => {
    if (localRows.length === 0) {
      showToast(t('errors.noDataToExport') || 'No data available to export', 'error');
      return;
    }

    // Filter out rows with only empty data
    const dataToExport = localRows
      .filter(row => row.item?._id || row.purchasePrice || row.quantity || row.serial || row.deadline)
      .map(row => ({
        [t("tableHeaders.idents")]: row.item?.ident || '',
        [t("tableHeaders.name")]: row.item?.name || '',
        [t('nestedTable.purchasePrice')]: asExcelNumber(row.purchasePrice),
        [t('nestedTable.valuationPrice')]: asExcelNumber(row.valuationPrice),
        [t('nestedTable.quantity')]: row.quantity || '',
        [t('nestedTable.serial')]: row.serial || '',
        [t('nestedTable.deadline')]: row.deadline || '',
        [t('nestedTable.priceValue')]: row.priceValue || ''
      }));

    if (dataToExport.length === 0) {
      showToast(t('errors.noDataToExport') || 'No data available to export', 'error');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const priceLabels = new Set([
      t('nestedTable.purchasePrice'),
      t('nestedTable.valuationPrice'),
    ]);
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const header = worksheet[XLSX.utils.encode_cell({ r: 0, c: column })];
      if (!header || !priceLabels.has(String(header.v))) continue;
      for (let row = 1; row <= range.e.r; row += 1) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
        if (cell?.t === 'n') cell.z = unitPriceExcelFormat();
      }
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Nested Document Table');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `nested_document_table_${timestamp}.xlsx`);

    showToast(t('success.excelExported') || 'Excel file exported successfully', 'success');
  };

  // Excel upload functionality
  const handleUploadExcel = async (rows: any[]) => {
    if (!rows || rows.length === 0) {
      showToast(t('errors.invalidExcelFile') || 'Invalid Excel file or no data found', 'error');
      return;
    }

    try {
      // Extract all unique idents from the Excel file
      const allIdents = rows
        .map(row => row[t("tableHeaders.idents")] || '')
        .filter(ident => ident?.trim())
        .map(ident => ident.trim());

      const uniqueIdents = Array.from(new Set(allIdents));

      // Create a map to store resolved items
      const itemMap = new Map();

      // Batch resolve all items using the new bulk lookup endpoint (SINGLE API CALL!)
      if (uniqueIdents.length > 0) {
        try {
          // Use the bulk lookup endpoint - ONE API call for all idents
          const items = await lookupItemsByIdents(uniqueIdents);

          // Build map from all returned items
          items.forEach((item: any) => {
            if (item.ident) {
              itemMap.set(item.ident.toLowerCase(), {
                _id: item._id,
                ident: item.ident || '',
                name: item.name || '',
                unitMeasurment: item.unitMeasurment || item.unitMeasurement || ''
              });
            }
          });
        } catch (error) {
          console.error('Bulk item lookup failed:', error);
        }
      }

      // Now process all rows using the pre-resolved items
      const mappedRows = rows.map((row) => {
        const ident = row[t("tableHeaders.idents")] || '';
        let resolvedItem = null;

        // Get item from our pre-resolved map
        if (ident?.trim()) {
          resolvedItem = itemMap.get(ident.trim().toLowerCase()) || null;
        }

        const newRow: DocumentLineItemWorkOrder = {
          document: documentId,
          item: resolvedItem,
          warehouse: buildDefaultWarehouse(), // Suggested warehouse default
          purchasePrice: row[t('nestedTable.purchasePrice')] || '',
          valuationPrice: row[t('nestedTable.valuationPrice')] || '',
          vat: '',
          forPayment: '',
          quantity: row[t('nestedTable.quantity')] || '',
          quantityOS: '',
          quantityWorkOrder: '',
          serial: row[t('nestedTable.serial')] || '',
          deadline: row[t('nestedTable.deadline')] || '',
          priceValue: row[t('nestedTable.priceValue')] || ''
        };

        return newRow;
      });

      // Filter out rows without valid items if needed
      const validRows = mappedRows.filter(row => row.item !== null || row.purchasePrice || row.quantity);

      if (validRows.length === 0) {
        showToast(t('errors.noValidDataFound') || 'No valid data found in Excel file', 'error');
        return;
      }

      // Replace current rows with uploaded data and preload items into cache
      setLocalRows(validRows);

      // Preload the resolved items into the ItemCache context to prevent future API calls
      // This is crucial to prevent the loading indicators after upload
      const itemsToCache = validRows
        .filter(row => row.item)
        .map(row => ({
          ...row.item!,
          ident: row.item!.ident || '',
          name: row.item!.name || '',
          unitMeasurment: row.item!.unitMeasurment || ''
        } as Item))
        .filter((item, index, self) => self.findIndex(i => i._id === item._id) === index); // Remove duplicates

      if (itemsToCache.length > 0) {
        // Add all resolved items to cache in one operation
        addItemsToCache(itemsToCache);
      }

      const resolvedCount = validRows.filter(row => row.item !== null).length;
      const totalRows = validRows.length;

      showToast(t('success.excelImported') || `Excel file imported successfully. ${totalRows} rows imported, ${resolvedCount} items resolved.`, 'success');

      // Focus on first row after import
      setTimeout(() => {
        const priceColumnIndex = columnOrder.indexOf('purchasePrice') + 2;
        const firstRowInput = inputRefs.current[0]?.[priceColumnIndex];
        if (firstRowInput) {
          firstRowInput.focus();
          if (firstRowInput instanceof HTMLInputElement) {
            firstRowInput.select();
          }
        }
      }, 100);

    } catch (error) {
      showToast(t('errors.excelImportFailed') || 'Failed to import Excel file', 'error');
    }
  };

  return {
    handleDownloadExcel,
    handleUploadExcel,
  };
}

export default useNestedDocumentExcel;
